var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx } from "nativewind/jsx-runtime";
import { useIsFocused } from '@react-navigation/native';
import { useAtomValue } from 'jotai';
import React from 'react';
import styled from 'styled-components/native';
import { getMarkerSvg, MarkerColors, ToiletMarkerColor } from '@/assets/markers';
import { currentLocationAtom } from '@/atoms/Location.ts';
import { useDevTool } from '@/components/DevTool/useDevTool';
import MapViewComponent from '@/components/maps/MapView';
import { getRegionCorners, getRegionFromItems, } from '@/components/maps/Types.tsx';
import { useLogger } from '@/logging/useLogger';
import { Platform } from 'react-native';
const ZOOM_CHANGE_THRESHOLD = 0.5;
const DefaultLatitudeDelta = 0.03262934222916414;
const DefaultLongitudeDelta = 0.03680795431138506;
// Corner marker constants
const CORNER_MARKER_PREFIX = 'debug-corner-';
// 마커 노출 로깅을 프레임당 몇 건씩 나눠 보낼지 (한 조각 ≈ 8ms).
const PIN_LOG_CHUNK_SIZE = 16;
function getRegion({ latitude, longitude }) {
    return {
        northEast: {
            latitude: latitude + DefaultLatitudeDelta / 2,
            longitude: longitude + DefaultLongitudeDelta / 2,
        },
        southWest: {
            latitude: latitude - DefaultLatitudeDelta / 2,
            longitude: longitude - DefaultLongitudeDelta / 2,
        },
    };
}
const SeoulStation = {
    latitude: 37.5559,
    longitude: 126.9723,
};
const DefaultRegion = getRegion(SeoulStation);
export default function ItemMap({ items, overlayMarkers, overlaySelectedId, onMarkerPress, onOverlayMarkerPress, mapRef, mapPadding, selectedItemId, onCameraIdle, logoPosition, markerIconOverride, }) {
    var _a;
    const [currentCameraRegion, setCurrentCameraRegion] = React.useState(null);
    const previousZoomRef = React.useRef(null);
    const previousCenterRef = React.useRef(null);
    const currentLocation = useAtomValue(currentLocationAtom);
    const region = currentLocation ? getRegion(currentLocation) : DefaultRegion;
    const devTool = useDevTool();
    // initialRegion 은 이름 그대로 **최초 1회** 값이다. 렌더마다 현위치로 다시 계산해
    // 내려보내면 안 된다.
    // - 네이티브는 첫 값만 채택하므로 이후 값은 어차피 무의미하고,
    // - iOS 는 그 "이미 적용됨" 플래그를 0.1초 지연 블록 **안에서** 세운다
    //   (RNTSccMapViewImpl.mm `setInitialRegion`). 그래서 지도 생성 직후 100ms 안에 위치가
    //   도착해 prop 이 한 번 더 바뀌면 **카메라 이동이 두 번 예약**되고, 나중 것(현위치)이 그
    //   사이에 실행된 fitToItems 를 덮는다. "앱 켜자마자 남은 매장 보기로 들어가면 현위치에
    //   고정된다"가 이 경로다 — 검색 화면은 fit 이 한참 뒤라 다시 덮어서 안 보였을 뿐이다.
    const initialRegionRef = React.useRef(null);
    if (initialRegionRef.current === null) {
        // 지도가 처음 뜰 때 이미 아이템이 있으면 **그 영역으로 시작**한다. 어차피 곧 fitToItems 가
        // 같은 곳으로 옮길 것이고, 무엇보다 iOS 는 initialRegion 을 0.1초 뒤에 적용해서
        // (RNTSccMapViewImpl.mm `setInitialRegion` 의 dispatch_after) 그 사이에 실행된 fit 을
        // 덮어버린다 — 목적지를 같게 맞춰 덮여도 화면이 달라지지 않게 한다.
        const base = items.length > 0 ? getRegionFromItems(items) : region;
        initialRegionRef.current = {
            northEastLat: base.northEast.latitude,
            northEastLng: base.northEast.longitude,
            southWestLat: base.southWest.latitude,
            southWestLng: base.southWest.longitude,
        };
    }
    const nativeRegion = initialRegionRef.current;
    const overlayCount = (_a = overlayMarkers === null || overlayMarkers === void 0 ? void 0 : overlayMarkers.length) !== null && _a !== void 0 ? _a : 0;
    // Overlay markers (화장실 레이어) — z-index는 항상 place markers보다 낮음
    const nativeOverlayMarkers = (overlayMarkers !== null && overlayMarkers !== void 0 ? overlayMarkers : []).map((item, index) => {
        var _a, _b, _c, _d, _e, _f, _g;
        const isSelected = item.id === overlaySelectedId;
        return {
            id: item.id,
            position: {
                lat: (_b = (_a = item.location) === null || _a === void 0 ? void 0 : _a.lat) !== null && _b !== void 0 ? _b : 0,
                lng: (_d = (_c = item.location) === null || _c === void 0 ? void 0 : _c.lng) !== null && _d !== void 0 ? _d : 0,
            },
            captionText: item.displayName,
            captionTextSize: 14,
            isHideCollidedMarkers: false,
            isHideCollidedSymbols: true,
            isHideCollidedCaptions: true,
            iconResource: getMarkerSvg((_f = (_e = item.markerIcon) === null || _e === void 0 ? void 0 : _e.icon) !== null && _f !== void 0 ? _f : 'default', isSelected, (_g = item.hasReview) !== null && _g !== void 0 ? _g : false),
            iconColor: ToiletMarkerColor,
            zIndex: isSelected
                ? overlayCount + items.length + 2
                : overlayCount - index,
        };
    });
    const nativeMarkerItems = items.map((item, index) => {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m;
        const isSelected = item.id === selectedItemId;
        const iconOverride = markerIconOverride === null || markerIconOverride === void 0 ? void 0 : markerIconOverride(item);
        return {
            id: item.id,
            position: {
                lat: (_b = (_a = item.location) === null || _a === void 0 ? void 0 : _a.lat) !== null && _b !== void 0 ? _b : 0,
                lng: (_d = (_c = item.location) === null || _c === void 0 ? void 0 : _c.lng) !== null && _d !== void 0 ? _d : 0,
            },
            captionText: item.displayName,
            captionTextSize: 14,
            isHideCollidedMarkers: false,
            isHideCollidedSymbols: true,
            isHideCollidedCaptions: true,
            iconResource: iconOverride
                ? ((_e = (isSelected ? iconOverride.focusedSvg : undefined)) !== null && _e !== void 0 ? _e : iconOverride.defaultSvg)
                : getMarkerSvg((_g = (_f = item.markerIcon) === null || _f === void 0 ? void 0 : _f.icon) !== null && _g !== void 0 ? _g : 'default', isSelected, (_h = item.hasReview) !== null && _h !== void 0 ? _h : false),
            iconColor: (_j = iconOverride === null || iconOverride === void 0 ? void 0 : iconOverride.iconColor) !== null && _j !== void 0 ? _j : (((_k = item.markerIcon) === null || _k === void 0 ? void 0 : _k.icon) === 'toilet'
                ? ToiletMarkerColor
                : MarkerColors[(_m = (_l = item.markerIcon) === null || _l === void 0 ? void 0 : _l.level) !== null && _m !== void 0 ? _m : 'none']),
            zIndex: isSelected
                ? overlayCount + items.length + 1
                : overlayCount + (items.length - index),
        };
    });
    // DevTool이 활성화되고 현재 카메라 영역이 있을 때 실제 보이는 영역의 귀퉁이 마커 생성
    const nativeCornerMarkers = React.useMemo(() => {
        if (!devTool.searchRegion.shouldShow() ||
            !currentCameraRegion ||
            !mapPadding) {
            return [];
        }
        const corners = getRegionCorners(currentCameraRegion);
        return corners.map((corner, index) => ({
            id: `${CORNER_MARKER_PREFIX}${index}`,
            position: {
                lat: corner.latitude,
                lng: corner.longitude,
            },
            captionText: `Corner ${index + 1}`,
            captionTextSize: 12,
            isHideCollidedMarkers: false,
            isHideCollidedSymbols: false,
            isHideCollidedCaptions: false,
            iconResource: getMarkerSvg('default', false, false),
            iconColor: '#FF0000', // 빨간색으로 구분
            zIndex: 50, // 일반 마커보다는 높지만 선택된 마커보다는 낮게
        }));
    }, [devTool.searchRegion.data]);
    // 모든 마커 합치기 (overlay → place → debug corner)
    const allNativeMarkers = [
        ...nativeOverlayMarkers,
        ...nativeMarkerItems,
        ...nativeCornerMarkers,
    ];
    // Create native overlays for DevTool
    const nativeCircleOverlays = [];
    const nativeRectangleOverlays = [];
    if (devTool.searchRegion.shouldShow() && devTool.searchRegion.data) {
        if (devTool.searchRegion.data.type === 'circle') {
            nativeCircleOverlays.push({
                id: 'debug-search-radius',
                center: {
                    lat: devTool.searchRegion.data.location.lat,
                    lng: devTool.searchRegion.data.location.lng,
                },
                radius: devTool.searchRegion.data.radiusMeters,
                fillColor: Platform.OS === 'ios' ? 'rgba(66, 165, 245, 0.15)' : '#2042A5F5', // 연한 파란색 배경
                strokeColor: Platform.OS === 'ios' ? 'rgba(66, 165, 245, 0.8)' : '#CC42A5F5', // 진한 파란색 테두리
                strokeWidth: 2,
            });
        }
        else if (devTool.searchRegion.data.type === 'rectangle') {
            nativeRectangleOverlays.push({
                id: 'debug-search-rectangle',
                leftTopLocation: {
                    lat: devTool.searchRegion.data.leftTopLocation.lat,
                    lng: devTool.searchRegion.data.leftTopLocation.lng,
                },
                rightBottomLocation: {
                    lat: devTool.searchRegion.data.rightBottomLocation.lat,
                    lng: devTool.searchRegion.data.rightBottomLocation.lng,
                },
                fillColor: Platform.OS === 'ios' ? 'rgba(76, 175, 80, 0.15)' : '#2049AF50', // 연한 초록색 배경
                strokeColor: Platform.OS === 'ios' ? 'rgba(76, 175, 80, 0.8)' : '#CC4CAF50', // 진한 초록색 테두리
                strokeWidth: 2,
            });
        }
    }
    const isFocused = useIsFocused();
    const logger = useLogger();
    const loggerRef = React.useRef(logger);
    loggerRef.current = logger;
    const loggedPinsRef = React.useRef(new Set());
    React.useEffect(() => {
        if (!isFocused)
            return;
        const pending = items.filter(item => !loggedPinsRef.current.has(item.id));
        if (pending.length === 0)
            return;
        pending.forEach(item => loggedPinsRef.current.add(item.id));
        // 마커가 수백 개인 화면(CTPL 정복 대상 361곳)에서 한 번에 돌면 브리지 호출이 JS 스레드를
        // 묶어 진입 직후 화면이 멈춘다(실측: 361건 175ms, __DEV__ 에선 logDebug 때문에 600ms+).
        // 프레임 사이로 나눠 보낸다 — 나가는 이벤트는 그대로다.
        let index = 0;
        let timer;
        const flush = () => {
            for (const item of pending.slice(index, index + PIN_LOG_CHUNK_SIZE)) {
                loggerRef.current.logElementView('search_item_marker', {
                    place_id: item.id,
                    place_name: item.displayName,
                });
            }
            index += PIN_LOG_CHUNK_SIZE;
            if (index < pending.length) {
                timer = setTimeout(flush, 0);
            }
        };
        timer = setTimeout(flush, 0);
        // 화면을 떠나면 남은 조각은 보내지 않는다 — 안 본 마커를 "봤다"고 기록하지 않는 편이 맞다.
        return () => clearTimeout(timer);
    }, [items, isFocused]);
    return (_jsx(StyledMapView, { initialRegion: nativeRegion, onMarkerPress: (x) => __awaiter(this, void 0, void 0, function* () {
            var _a;
            // 디버그 귀퉁이 마커는 무시
            if (x.nativeEvent.id.startsWith(CORNER_MARKER_PREFIX)) {
                return;
            }
            // overlay marker (화장실 레이어) 클릭 분기
            const overlayItem = overlayMarkers === null || overlayMarkers === void 0 ? void 0 : overlayMarkers.find(it => it.id === x.nativeEvent.id);
            if (overlayItem) {
                onOverlayMarkerPress === null || onOverlayMarkerPress === void 0 ? void 0 : onOverlayMarkerPress(overlayItem);
                yield logger.logElementClick('toilet_layer_marker', {
                    external_accessibility_id: overlayItem.id,
                    place_name: overlayItem.displayName,
                });
                return;
            }
            const item = items.find(it => it.id === x.nativeEvent.id);
            if (!item) {
                return;
            }
            onMarkerPress === null || onMarkerPress === void 0 ? void 0 : onMarkerPress(item);
            yield logger.logElementClick('search_item_marker', {
                place_id: item.id,
                place_name: item.displayName,
                place_score_level: (_a = item.markerIcon) === null || _a === void 0 ? void 0 : _a.level,
            });
        }), ref: mapRef, onCameraIdle: ({ nativeEvent }) => {
            var _a, _b;
            const newRegion = {
                northEast: {
                    latitude: nativeEvent.northEastLat,
                    longitude: nativeEvent.northEastLng,
                },
                southWest: {
                    latitude: nativeEvent.southWestLat,
                    longitude: nativeEvent.southWestLng,
                },
            };
            setCurrentCameraRegion(newRegion);
            onCameraIdle === null || onCameraIdle === void 0 ? void 0 : onCameraIdle(newRegion, nativeEvent.reason);
            // reason: 0=gesture, 1=control, 2=location, 3=developer
            if (nativeEvent.reason !== 0) {
                previousZoomRef.current = nativeEvent.zoom;
                previousCenterRef.current = {
                    lat: nativeEvent.centerLat,
                    lng: nativeEvent.centerLng,
                };
                return;
            }
            const currentZoom = nativeEvent.zoom;
            const previousZoom = previousZoomRef.current;
            const latDelta = nativeEvent.northEastLat - nativeEvent.southWestLat;
            const visibleRangeM = Math.round(latDelta * 111320);
            let eventName;
            if (previousZoom !== null) {
                const zoomDiff = currentZoom - previousZoom;
                if (zoomDiff > ZOOM_CHANGE_THRESHOLD) {
                    eventName = 'map_zoom_in';
                }
                else if (zoomDiff < -ZOOM_CHANGE_THRESHOLD) {
                    eventName = 'map_zoom_out';
                }
                else {
                    eventName = 'map_camera_move';
                }
            }
            else {
                eventName = 'map_camera_move';
            }
            logger.logElementClick(eventName, {
                before_center_lat: (_a = previousCenterRef.current) === null || _a === void 0 ? void 0 : _a.lat,
                before_center_lng: (_b = previousCenterRef.current) === null || _b === void 0 ? void 0 : _b.lng,
                after_center_lat: nativeEvent.centerLat,
                after_center_lng: nativeEvent.centerLng,
                before_zoom: previousZoom,
                after_zoom: currentZoom,
                visible_range_m: visibleRangeM,
            });
            previousZoomRef.current = currentZoom;
            previousCenterRef.current = {
                lat: nativeEvent.centerLat,
                lng: nativeEvent.centerLng,
            };
        }, mapPadding: mapPadding, markers: allNativeMarkers, circleOverlays: nativeCircleOverlays, rectangleOverlays: nativeRectangleOverlays, logoPosition: logoPosition }));
}
const StyledMapView = styled(MapViewComponent) `
  position: absolute;
  top: 0;
  width: 100%;
  height: 100%;
`;
